import { fieldKey } from "@gjwtrack/query-language";
import { useMemo } from "react";
import { Link } from "react-router";
import { useClient } from "../lib/client.ts";
import { absTime, BUNDLE_KINDS, chipClass, fieldValueText, issueKey, relTime, userName } from "../lib/format.ts";

/** One row of an issue list: key · summary · tags · option-set chips · reporter · updated. Reads the issue from the client store, so it stays live. */
export function IssueRow({ id }: { id: number }) {
  const issue = useClient((s) => s.display[id]);
  const schema = useClient((s) => s.schema);
  const pending = useClient((s) => s.pending.some((p) => p.issueId === id));
  const chips = useMemo(() => {
    if (!issue || !schema) return [];
    const out: { key: string; cls: string; text: string }[] = [];
    for (const pf of schema.projectFieldsByProject.get(issue.projectId) ?? []) {
      const def = schema.fieldsById.get(pf.fieldId);
      if (!def || !BUNDLE_KINDS.has(def.kind)) continue;
      for (const val of issue.fields[fieldKey(def.id)] ?? []) out.push({ key: `${def.id}:${val}`, cls: chipClass(schema, def, val), text: fieldValueText(schema, def, val) });
    }
    return out;
  }, [issue, schema]);
  if (!issue || !schema) return null;
  const key = issueKey(schema, issue);
  return (
    <Link
      to={`/issue/${key}`}
      data-issue-id={issue.id}
      data-issue-key={key}
      className={`flex h-full items-center gap-3 border-b border-line px-4 hover:bg-surface ${pending ? "opacity-70" : ""}`}
    >
      <span className={`w-24 shrink-0 font-mono text-xs ${issue.resolvedAt !== null ? "text-faint line-through" : "text-muted"}`}>{key}</span>
      <span className={`min-w-0 flex-1 truncate text-[14px] ${issue.resolvedAt !== null ? "text-muted line-through decoration-faint" : ""}`}>{issue.summary}</span>
      {issue.tags.length > 0 && (
        <span className="row-tags flex shrink-0 gap-1">
          {issue.tags.map((t) => (
            <span key={t} className="chip">
              {schema.tagsById.get(t)?.name ?? `#${t}`}
            </span>
          ))}
        </span>
      )}
      <span className="flex shrink-0 gap-1">
        {chips.map((c) => (
          <span key={c.key} className={c.cls}>
            {c.text}
          </span>
        ))}
      </span>
      <span className="w-28 shrink-0 truncate text-xs text-muted">{userName(schema, issue.reporterId)}</span>
      <span className="w-14 shrink-0 text-right text-xs text-muted tabular-nums" title={absTime(issue.updatedAt)}>
        {relTime(issue.updatedAt)}
      </span>
    </Link>
  );
}
